/**
 * Actions bar for Sent tab.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC, ReactNode } from 'react';
import { CheckSquare, Square } from 'lucide-react';

import { Button } from '@/components/ui';
import { PreparedDropsUndoButton } from '@/features/prepared-drops-undo';

import { CountryGroupSortToggle } from './CountryGroupSortToggle';
import { SentSelectionActionButtons } from './SentSelectionActionButtons';

interface SentActionsBarProps {
  hasSelection: boolean;
  selectedCount: number;
  totalCount: number;
  allSelected: boolean;
  isBusy: boolean;
  canKillOrEdit: boolean;
  selectedWithSentIdCount: number;
  onToggleAll: () => void;
  onCopyAsDraft?: () => void;
  onKill?: () => void;
  onEdit?: () => void;
  onSaveAsPreset?: () => void;
  resendSlot?: ReactNode;
  onClearSelection: () => void;
  hasCopiedValues?: boolean;
  onApplyCopied?: () => void;
  listGroupByCountry?: boolean;
  onListGroupByCountryChange?: (value: boolean) => void;
}

export const SentActionsBar: FC<SentActionsBarProps> = ({
  hasSelection,
  selectedCount,
  totalCount,
  allSelected,
  isBusy,
  canKillOrEdit,
  selectedWithSentIdCount,
  onToggleAll,
  onCopyAsDraft,
  onKill,
  onEdit,
  onSaveAsPreset,
  resendSlot,
  onClearSelection,
  hasCopiedValues,
  onApplyCopied,
  listGroupByCountry = false,
  onListGroupByCountryChange,
}) => {
  const showSelectionButtons =
    canKillOrEdit ||
    (onCopyAsDraft && hasSelection) ||
    (onSaveAsPreset && hasSelection) ||
    (onApplyCopied && hasCopiedValues && hasSelection);

  return (
    <div className="shrink-0 border-b border-border/40 bg-muted/10 px-4 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={onToggleAll}
            disabled={isBusy || totalCount === 0}
            aria-label="Select all sent"
          >
            {allSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
            Select all
            <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium tabular-nums">{totalCount}</span>
          </Button>
          {onListGroupByCountryChange != null && (
            <CountryGroupSortToggle active={listGroupByCountry} onChange={onListGroupByCountryChange} />
          )}
          {/* The undo stack is per USER, not per tab: a copy-as-draft made from
           *  here, or an edit undone from here, is the same last action. */}
          <PreparedDropsUndoButton />
        </div>

        {showSelectionButtons ? (
          <SentSelectionActionButtons
            selectedCount={selectedCount}
            selectedWithSentIdCount={selectedWithSentIdCount}
            isBusy={isBusy}
            onApplyCopied={onApplyCopied}
            hasCopiedValues={hasCopiedValues}
            onCopyAsDraft={onCopyAsDraft}
            onSaveAsPreset={onSaveAsPreset}
            resendSlot={resendSlot}
            onKill={onKill}
            onEdit={onEdit}
            onClearSelection={onClearSelection}
          />
        ) : (
          <div className="text-xs text-muted-foreground">Select sent drops to copy as draft, kill, edit or re-send.</div>
        )}
      </div>
    </div>
  );
};
